import { createFileRoute } from "@tanstack/react-router";
import type {
  DescribedSchema,
  InterfaceSchema,
  IntersectionSchema,
  LiteralSchema,
  OmitSchema,
  Schema,
  TypeIdentifierSchema,
  UnionSchema,
  SchemaMetadata,
  EnumSchema,
  NominalNumberSchema,
} from "common/data/schema";
import { buildingQuerySchemas } from "schemas-common/buildingQuery";

function BuildingAPIViewer() {
  return (
    <div style={{ padding: "20px" }}>
      <h1>Building API Schema Viewer</h1>
      {Object.entries(buildingQuerySchemas).map(([route, entitySchema]) => {
        return (
          <div key={route}>
            <h2>{route}</h2>
            {Object.entries(entitySchema.fields).map(([prop, propSchema]) => {
              return (
                <div key={route}>
                  <SchemaViewer schema={propSchema} name={prop} />
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

export function InterfaceSchemaViewer({ schema, name: interfaceName }: { schema: InterfaceSchema; name?: string }) {
  return (
    <span>
      {interfaceName && (
        <b>
          <a id={`${interfaceName}`}>{interfaceName}</a>
        </b>
      )}
      :{" "}
      {Object.entries(schema.fields).map(([name, value]) => {
        return (
          <span key={name}>
            <SchemaViewer schema={value} name={name} />
          </span>
        );
      })}
    </span>
  );
}

export function SchemaViewer({ schema, name }: { schema: Schema; name?: string }) {
  if (!schema) {
    return <span>null</span>;
  }
  switch (schema.kind) {
    case "INTERFACE":
      return <InterfaceSchemaViewer schema={schema as InterfaceSchema} name={name} />;
    case "ANY":
      return (
        <span>
          <ParamName name={name} />
        </span>
      );
    case "NULLISH":
      return (
        <span>
          <ParamName name={name} />
          {schema.subType}
        </span>
      );
    case "BOOLEAN":
      return (
        <span>
          <ParamName name={name} />
          boolean
        </span>
      );
    case "NUMBER":
      return (
        <span>
          <ParamName name={name} />
          number
        </span>
      );
    case "STRING":
      return (
        <span>
          <ParamName name={name} />
          string
        </span>
      );
    case "UNION":
      return <UnionSchemaViewer schema={schema as UnionSchema} name={name} />;
    case "INTERSECTION":
      return <IntersectionSchemaViewer schema={schema as IntersectionSchema} name={name} />;
    case "OMIT":
      return <OmitSchemaViewer schema={schema as OmitSchema} name={name} />;
    case "LITERAL":
      return <LiteralSchemaViewer schema={schema as LiteralSchema} name={name} />;
    case "IDENTIFIER":
      return <IdentifierSchemaViewer schema={schema as TypeIdentifierSchema} name={name} />;
    case "ENUM":
      return <EnumSchemaViewer schema={schema as EnumSchema} name={name} />;
    case "NOMINAL_NUMBER":
      return <NominalNumberSchemaViewer schema={schema as NominalNumberSchema} name={name} />;
    case "DELETED":
      return (
        <span>
          <ParamName name={name} />
          <span style={{ color: "red" }}>&lt;Deleted&gt;</span>
          <MetadataViewer metadata={schema.metadata} />
        </span>
      );
    case "MOVED":
      return (
        <span>
          <ParamName name={name} />
          <span style={{ color: "orange" }}>&lt;Moved to {schema.movedTo}&gt;</span>
          <MetadataViewer metadata={schema.metadata} />
        </span>
      );
    case "DESCRIBED":
      return <DescribedSchemaViewer schema={schema as DescribedSchema} name={name} />;
    default:
      return (
        <span>
          <ParamName name={name} />
          Unknown schema {schema.kind}
        </span>
      );
  }
}

function ParamName({ name }: { name: string | undefined }) {
  if (!name) {
    return null;
  }
  return (
    <span>
      <b>{name}</b>:{" "}
    </span>
  );
}

function SchemaAnnotation({ children }: { children: React.ReactNode }) {
  return <span style={{ color: "gray", fontStyle: "italic", fontSize: "10px" }}>&nbsp;{children}</span>;
}

function MetadataViewer({ metadata }: { metadata: SchemaMetadata }) {
  return (
    <SchemaAnnotation>
      {metadata.comment} @ {metadata.date}
    </SchemaAnnotation>
  );
}

function UnionSchemaViewer({ schema, name }: { schema: UnionSchema; name?: string }) {
  return (
    <span>
      <ParamName name={name} />
      {schema.unionTypes.map((t) => {
        return (
          <span key={t.kind}>
            {" "}
            | <SchemaViewer schema={t} name={schema.name} />
          </span>
        );
      })}
    </span>
  );
}

function IntersectionSchemaViewer({ schema, name }: { schema: IntersectionSchema; name?: string }) {
  return (
    <span>
      <ParamName name={name} />
      {schema.intersectionTypes.map((t) => {
        return (
          <span key={t.kind}>
            {" "}
            & <SchemaViewer schema={t} />
          </span>
        );
      })}
    </span>
  );
}

function OmitSchemaViewer({ schema, name }: { schema: OmitSchema; name?: string }) {
  if (schema.fields.kind === "UNION") {
    return (
      <span>
        <ParamName name={name} />
        {schema.fields.unionTypes.map((t) => {
          return (
            <span key={t.kind}>
              {" "}
              & <SchemaViewer schema={t} />
            </span>
          );
        })}
      </span>
    );
  }
  if (schema.fields.kind === "LITERAL") {
    return (
      <span>
        <ParamName name={name} /> {schema.fields.value}
      </span>
    );
  }
  return null;
}

function LiteralSchemaViewer({ schema, name }: { schema: LiteralSchema; name?: string }) {
  return (
    <span>
      <ParamName name={name} /> {schema.value}
    </span>
  );
}

function IdentifierSchemaViewer({ schema, name }: { schema: TypeIdentifierSchema; name?: string }) {
  return (
    <span>
      <ParamName name={name} /> <a href={`#${schema.name}`}>{schema.name}</a>
    </span>
  );
}

function EnumSchemaViewer({ schema, name }: { schema: EnumSchema; name?: string }) {
  return (
    <span>
      <ParamName name={name} /> {schema.name} [
      {Object.entries(schema.members)
        .map(([key, value]) => `${key}: ${value}`)
        .join(", ")}
      ]
    </span>
  );
}

function DescribedSchemaViewer({ schema, name }: { schema: DescribedSchema; name?: string }) {
  return (
    <span>
      <SchemaViewer schema={schema.schema} name={name} />
      <DescriptionViewer description={schema.description} />
    </span>
  );
}

function DescriptionViewer({ description }: { description: string }) {
  return <SchemaAnnotation>{description}</SchemaAnnotation>;
}

function NominalNumberSchemaViewer({ schema, name }: { schema: NominalNumberSchema; name?: string }) {
  return (
    <span>
      <ParamName name={name} /> {schema.name}
    </span>
  );
}

export const Route = createFileRoute("/debug/schema/building-api")({
  component: BuildingAPIViewer,
});
